import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Building2, Layout, Search, Star, Trash2, X } from 'lucide-react';
import { RelationChips, RelationChip } from './RelationChips';
import { useIntegrationGraph } from '../../../hooks/useIntegrationGraph';

interface HotelAccount {
  id: string;
  email: string;
  company_name?: string | null;
  hotel_name?: string | null;
  hotel_code?: string | null;
  warehouse_enabled?: boolean;
  category: string;
  city: string;
  country: string;
  created_at: string;
  updated_at: string;
  total_integrations: number;
}

function getHotelDisplayName(account: HotelAccount): string {
  return (account.company_name ?? account.hotel_name ?? '').trim() || '—';
}

interface HotelAccountsProps {
  /** Search term pushed in when arriving from another listing's relation chip. */
  initialSearch?: string | null;
  onInitialSearchConsumed?: () => void;
  onOpenConnector?: (connectorCode: string) => void;
  onOpenSaas?: (saasCompany: string) => void;
}

export function HotelAccounts({
  initialSearch,
  onInitialSearchConsumed,
  onOpenConnector,
  onOpenSaas,
}: HotelAccountsProps = {}) {
  const [accounts, setAccounts] = useState<HotelAccount[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accountToDelete, setAccountToDelete] = useState<HotelAccount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingWarehouseId, setTogglingWarehouseId] = useState<string | null>(null);
  const [connectedSaasFor, setConnectedSaasFor] = useState<HotelAccount | null>(null);
  const {
    graph,
    emptyLabel: relationEmptyLabel,
    emptyVariant: relationEmptyVariant,
    connectorEmptyLabel,
    connectorEmptyVariant,
  } = useIntegrationGraph();

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (initialSearch === undefined || initialSearch === null) return;
    setSearchTerm(initialSearch);
    onInitialSearchConsumed?.();
  }, [initialSearch, onInitialSearchConsumed]);

  const loadAccounts = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('hotel_accounts_secure')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // Ensure total_integrations is 0 when null
      const processedData = (data || []).map(account => ({
        ...account,
        total_integrations: account.total_integrations || 0
      }));

      setAccounts(processedData);
    } catch (err) {
      setError('Failed to load hotel accounts');
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const term = searchTerm.toLowerCase();
    const name = (account.company_name ?? account.hotel_name ?? '').toLowerCase();
    const email = (account.email ?? '').toLowerCase();
    // Relation chips fall back to the hotel code when a hotel has no name.
    const code = (account.hotel_code ?? '').toLowerCase();
    return name.includes(term) || email.includes(term) || code.includes(term);
  });

  const handleWarehouseToggle = async (account: HotelAccount, enabled: boolean) => {
    setTogglingWarehouseId(account.id);
    setError('');
    const previous = account.warehouse_enabled === true;
    setAccounts(current =>
      current.map(item =>
        item.id === account.id ? { ...item, warehouse_enabled: enabled } : item,
      ),
    );

    try {
      const { error: updateError } = await supabase
        .from('hotel_details')
        .update({ warehouse_enabled: enabled })
        .eq('id', account.id);

      if (updateError) throw updateError;
    } catch (err: unknown) {
      setAccounts(current =>
        current.map(item =>
          item.id === account.id ? { ...item, warehouse_enabled: previous } : item,
        ),
      );
      const message = err instanceof Error ? err.message : 'Failed to update warehouse opt-in';
      setError(message);
      console.error('Warehouse opt-in toggle error:', err);
    } finally {
      setTogglingWarehouseId(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeleting(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-delete-account', {
        body: { userId: accountToDelete.id, userType: 'hotel' },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setAccountToDelete(null);
      await loadAccounts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      setError(message);
      console.error('Delete hotel account error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const connectorChipsFor = (hotelId: string): RelationChip[] =>
    (graph.byHotelId.get(hotelId)?.connectors ?? []).map(connector => ({
      key: connector.code,
      label: connector.name,
      hint: connector.connector_hotel_id,
      status: connector.status,
    }));

  const saasChipsFor = (hotelId: string): RelationChip[] =>
    (graph.byHotelId.get(hotelId)?.saas_accounts ?? []).map(saas => ({
      key: saas.id,
      label: saas.company_name,
      status: saas.status,
    }));

  const renderStars = (category: string) => {
    const stars = [];
    const numStars = parseInt(category, 10);
    
    for (let i = 0; i < numStars; i++) {
      stars.push(
        <Star 
          key={i} 
          className="h-4 w-4 text-yellow-400 inline-block" 
          fill="currentColor"
        />
      );
    }
    
    return (
      <div className="flex items-center space-x-0.5">
        {stars}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading hotel accounts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div>
          <div className="flex items-center space-x-2">
            <Building2 className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">Hotel Accounts</h2>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Warehouse collection is opt-in. Only hotels with the toggle on have reservations stored.
          </p>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            {error}
          </div>
        )}

        <div className="mb-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by hotel name or email"
                  className="form-input pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header px-4">Hotel Name</th>
                <th className="table-header px-4">Hotel Code</th>
                <th className="table-header px-4">Connector</th>
                <th className="table-header px-4">Category</th>
                <th className="table-header px-4">Location</th>
                <th className="table-header px-4">Created</th>
                <th className="table-header px-4">Connected SaaS</th>
                <th className="table-header px-4">Warehouse</th>
                <th className="table-header px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredAccounts.map((account) => (
                <tr key={account.id} className="table-row">
                  <td className="table-cell px-4 whitespace-normal">
                    {getHotelDisplayName(account)}
                  </td>
                  <td className="table-cell px-4 font-mono text-sm">
                    {account.hotel_code ?? '—'}
                  </td>
                  <td className="table-cell px-4 whitespace-normal">
                    <RelationChips
                      chips={connectorChipsFor(account.id)}
                      onSelect={onOpenConnector ? chip => onOpenConnector(chip.key) : undefined}
                      emptyLabel={connectorEmptyLabel('No connector')}
                      emptyVariant={connectorEmptyVariant}
                    />
                  </td>
                  <td className="table-cell px-4">
                    {renderStars(account.category)}
                  </td>
                  <td className="table-cell px-4 whitespace-normal">
                    {account.city}, {account.country}
                  </td>
                  <td className="table-cell px-4">
                    {new Date(account.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell px-4 whitespace-normal">
                    <RelationChips
                      chips={saasChipsFor(account.id)}
                      onSelect={onOpenSaas ? chip => onOpenSaas(chip.label) : undefined}
                      emptyLabel={relationEmptyLabel('No SaaS connected')}
                      emptyVariant={relationEmptyVariant}
                    />
                  </td>
                  <td className="table-cell px-4">
                    <button
                      type="button"
                      onClick={() =>
                        handleWarehouseToggle(account, !(account.warehouse_enabled === true))
                      }
                      disabled={togglingWarehouseId === account.id}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        account.warehouse_enabled === true ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                      title={
                        account.warehouse_enabled === true
                          ? 'Disable warehouse collection'
                          : 'Enable warehouse collection'
                      }
                      aria-label={
                        account.warehouse_enabled === true
                          ? `Disable warehouse for ${getHotelDisplayName(account)}`
                          : `Enable warehouse for ${getHotelDisplayName(account)}`
                      }
                    >
                      {togglingWarehouseId === account.id ? (
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-white">
                          ...
                        </span>
                      ) : (
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            account.warehouse_enabled === true ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      )}
                    </button>
                  </td>
                  <td className="table-cell px-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConnectedSaasFor(account)}
                        className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                        title="Connected SaaS"
                        aria-label={`Connected SaaS for ${getHotelDisplayName(account)}`}
                      >
                        <Layout className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountToDelete(account)}
                        className="inline-flex items-center px-2 py-1 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                        title="Delete account"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAccounts.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm
                  ? 'No accounts found matching your search criteria.'
                  : 'No hotel accounts found.'}
              </p>
            </div>
          )}
        </div>

        {connectedSaasFor && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connected-saas-title"
          >
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="flex items-start justify-between border-b border-gray-200 p-4">
                <div>
                  <h3 id="connected-saas-title" className="text-lg font-semibold text-gray-900">
                    Connected SaaS
                  </h3>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {getHotelDisplayName(connectedSaasFor)}
                    {connectedSaasFor.hotel_code ? ` · ${connectedSaasFor.hotel_code}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConnectedSaasFor(null)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-80 overflow-y-auto p-4">
                {saasChipsFor(connectedSaasFor.id).length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {relationEmptyLabel('This hotel is not connected to any SaaS account.')}
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {saasChipsFor(connectedSaasFor.id).map(chip => (
                      <li key={chip.key} className="flex items-center justify-between py-2">
                        <button
                          type="button"
                          onClick={() => {
                            setConnectedSaasFor(null);
                            onOpenSaas?.(chip.label);
                          }}
                          disabled={!onOpenSaas}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-900 disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                        >
                          {chip.label}
                        </button>
                        <span
                          className={`status-badge ${
                            chip.status === 'active' ? 'status-active' : 'status-inactive'
                          }`}
                        >
                          {chip.status ?? 'unknown'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {accountToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="delete-hotel-title">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 id="delete-hotel-title" className="text-lg font-semibold text-gray-900">Delete hotel account?</h3>
              <p className="mt-2 text-sm text-gray-600">
                Delete the account for <strong>{getHotelDisplayName(accountToDelete)}</strong> ({accountToDelete.email})? This cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setAccountToDelete(null)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}